import React, { useCallback, useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, AlertCircle } from 'lucide-react';
import { type Question } from '../../content';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { AssessmentHeader } from './AssessmentHeader';
import { AssessmentQuestionCard } from './AssessmentQuestionCard';
import { AssessmentMCOptions } from './AssessmentMCOptions';
import { AssessmentIntroScreen } from './AssessmentIntroScreen';
import { AssessmentCompleteScreen } from './AssessmentCompleteScreen';
import { useEscapeKey } from '../../app/hooks/useEscapeKey';
import { useFocusTrap } from '../../app/hooks/useFocusTrap';
import { error as logError } from '../../utils/logger';
import type { AssessmentDomainId } from '../types';
interface AssessmentSessionProps {
  questions: Question[];
  domainId?: AssessmentDomainId;
  onComplete: () => void;
  onExit: () => void;
  onAnswer: (questionId: string, isCorrect: boolean, userChoice: number, payload?: string, errorType?: string, delta?: number) => void;
  /**
   * R2-L1 (Assessment audit 2026): Fired whenever the visible question
   * changes WITHOUT an answer being recorded (e.g. Previous navigation).
   * The parent hook uses this to reset its per-question timer so
   * `timeSpent` is not inflated by review time.
   */
  onQuestionChange?: () => void;
  /**
   * R2-RESUME (F-AS-R1-RESUME-LOSS, May 2026): When the user resumes a
   * recovered assessment, the parent provides:
   *  - `startIndex`: which question to land on (first unanswered),
   *  - `initialChoices`: previously selected option per question index,
   *  - `initialAnsweredIndices`: which indices already have an answer
   *    recorded server-side (used to render the progress dots),
   *  - `skipIntro`: skip the intro screen and go straight to questions.
   */
  startIndex?: number;
  initialChoices?: Record<number, number>;
  initialAnsweredIndices?: number[];
  skipIntro?: boolean;
}
type SessionPhase = 'intro' | 'questions' | 'complete';
export function AssessmentSession({
  questions,
  domainId = 'rekenen',
  // fallback
  onComplete,
  onExit,
  onAnswer,
  onQuestionChange,
  startIndex = 0,
  initialChoices,
  initialAnsweredIndices,
  skipIntro = false
}: AssessmentSessionProps) {
  const [phase, setPhase] = useState<SessionPhase>(skipIntro ? 'questions' : 'intro');
  const [currentIndex, setCurrentIndex] = useState(startIndex);
  const [selectedOptions, setSelectedOptions] = useState<Record<number, number>>(() => initialChoices ? {
    ...initialChoices
  } : {});
  const [answeredQuestions, setAnsweredQuestions] = useState<Set<number>>(() => new Set(initialAnsweredIndices ?? []));
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const exitModalRef = useRef<HTMLDivElement>(null);
  useFocusTrap(showExitConfirm, exitModalRef);
  const [elapsedMs, setElapsedMs] = useState(0);
  const startTimeRef = useRef(Date.now());
  const currentQuestion = questions[currentIndex];
  const isLastQuestion = currentIndex === questions.length - 1;
  const selectedOriginalIndex = selectedOptions[currentIndex] ?? null;
  // Global Timer — starts when entering questions phase
  useEffect(() => {
    if (phase !== 'questions') return;
    startTimeRef.current = Date.now();
    const interval = setInterval(() => {
      setElapsedMs(Date.now() - startTimeRef.current);
    }, 1000);
    return () => clearInterval(interval);
  }, [phase]);
  // Escape key for exit
  useEscapeKey(() => {
    if (phase === 'questions') setShowExitConfirm(true);
  });
  // Navigation
  const handleNext = useCallback(() => {
    if (selectedOriginalIndex === null) return;
    // Record answer
    const isCorrect = selectedOriginalIndex === currentQuestion.correctAnswer;
    onAnswer(currentQuestion.id, isCorrect, selectedOriginalIndex);
    setAnsweredQuestions(prev => new Set(prev).add(currentIndex));
    if (isLastQuestion) {
      // Capture exact elapsed time before transitioning — the interval
      // only ticks every 1 s, so elapsedMs could be up to ~1 s behind.
      setElapsedMs(Date.now() - startTimeRef.current);
      setPhase('complete');
    } else {
      setCurrentIndex(prev => prev + 1);
    }
  }, [selectedOriginalIndex, currentQuestion, currentIndex, isLastQuestion, onAnswer]);
  const handlePrevious = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
      onQuestionChange?.();
    }
  }, [currentIndex, onQuestionChange]);
  const handleSelect = useCallback((originalIndex: number) => {
    setSelectedOptions(prev => ({
      ...prev,
      [currentIndex]: originalIndex
    }));
  }, [currentIndex]);
  // Enter key to submit
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (phase === 'questions' && e.key === 'Enter' && selectedOriginalIndex !== null && !showExitConfirm) {
        handleNext();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [phase, selectedOriginalIndex, showExitConfirm, handleNext]);
  if (!currentQuestion && phase === 'questions') {
    logError('AssessmentSession: No current question found');
    return null;
  }
  if (phase === 'intro') {
    return <AssessmentIntroScreen domainId={domainId} questionCount={questions.length} onStart={() => {
      setPhase('questions');
      startTimeRef.current = Date.now();
    }} onCancel={onExit} data-id="element-976" data-mp="2m2apfh0p2sqf" />;
  }
  if (phase === 'complete') {
    return <AssessmentCompleteScreen questionCount={questions.length} elapsedMs={elapsedMs} domainId={domainId} onViewReport={onComplete} data-id="element-977" data-mp="2zu7e1b35mki2" />;
  }
  return <div className="min-h-screen bg-gradient-to-b from-indigo-50/80 to-slate-50 flex flex-col font-sans h-screen overflow-hidden" data-id="element-978" data-mp="14qslom2ga0hn">
      <AssessmentHeader currentQuestion={currentIndex + 1} totalQuestions={questions.length} elapsedMs={elapsedMs} onExit={() => setShowExitConfirm(true)} onPrevious={handlePrevious} canGoPrevious={currentIndex > 0} answeredIndices={answeredQuestions} data-id="element-979" data-mp="zsr0nvlgrr3j" />

      <div className="flex-1 max-w-4xl mx-auto w-full px-4 sm:px-6 py-6 mt-16 flex flex-col min-h-0 overflow-y-auto pb-24" data-id="element-980" data-mp="1nfz8olaa1e99">
        <div className="flex-1 flex flex-col relative justify-center" data-id="element-981" data-mp="1jhrmvc0yhmr7">
          <AnimatePresence mode="wait" data-id="element-982" data-mp="1xe52sgpudg3k">
            <motion.div key={currentQuestion.id} initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} transition={{
            duration: 0.2
          }} className="w-full" data-id="element-983" data-mp="1sgdf5yw8cg56">
              <AssessmentQuestionCard question={currentQuestion} data-id="element-984" data-mp="2eo949uy9z8lz">
                <AssessmentMCOptions questionId={currentQuestion.id} options={currentQuestion.options || []} onSelect={handleSelect} selectedOriginalIndex={selectedOriginalIndex} data-id="element-985" data-mp="200o1k25bbbel" />
              </AssessmentQuestionCard>

              <motion.div initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.1
            }} className="mt-8 flex justify-center" data-id="element-986" data-mp="1vzc79jbhu202">
                <Button size="xl" variant="primary" disabled={selectedOriginalIndex === null} onClick={handleNext} className="w-full sm:w-auto sm:min-w-[320px] shadow-lg rounded-2xl text-fluid-lg font-bold" data-id="element-987" data-mp="3gdw4bxuwwv9g">
                  <span className="mr-2" data-id="element-988" data-mp="1ue7lmd96pxc3">
                    {isLastQuestion ? 'Toets Afronden' : 'Volgende'}
                  </span>
                  <ArrowRight className="w-5 h-5" strokeWidth={3} data-id="element-989" data-mp="1pfmm6g1rbhop" />
                </Button>
              </motion.div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Assessment Exit Modal */}
        <AnimatePresence data-id="element-990" data-mp="1ghkoa9dbl55n">
          {showExitConfirm && <>
              <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50" onClick={() => setShowExitConfirm(false)} data-id="element-991" data-mp="1bgdiugyngi25" />
              <motion.div ref={exitModalRef} role="dialog" aria-modal="true" aria-labelledby="assessment-exit-title" aria-describedby="assessment-exit-desc" initial={{
            opacity: 0,
            scale: 0.9,
            y: 20
          }} animate={{
            opacity: 1,
            scale: 1,
            y: 0
          }} exit={{
            opacity: 0,
            scale: 0.9,
            y: 20
          }} transition={{
            type: 'spring',
            bounce: 0.2,
            duration: 0.4
          }} className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-992" data-mp="38eatpge7keag">
                <Card className="max-w-md w-full p-8 shadow-2xl border border-slate-200 bg-white rounded-3xl" data-id="element-993" data-mp="jlf9dxy7v5cc">
                  <div className="text-center" data-id="element-994" data-mp="1mju56edpx7ep">
                    <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-995" data-mp="1qlp81nbarsul">
                      <AlertCircle className="w-7 h-7 text-amber-600" data-id="element-996" data-mp="1vcdx6vzi8dqk" />
                    </div>
                    <h2 id="assessment-exit-title" className="text-2xl font-black text-slate-900 mb-3 tracking-tight" data-id="element-997" data-mp="20c0bz9uze21h">
                      Toets stoppen?
                    </h2>
                    <p id="assessment-exit-desc" className="text-slate-600 font-medium mb-6 leading-relaxed" data-id="element-998" data-mp="17p5sog9wh8pl">
                      Je voortgang wordt opgeslagen als onvolledig. Je kunt
                      later opnieuw beginnen.
                    </p>
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mb-6 text-left" data-id="element-999" data-mp="2t0fljgvcfs5f">
                      <p className="text-sm text-slate-700 font-medium" data-id="element-1000" data-mp="hcsxtoyby3h0">
                        Je hebt {answeredQuestions.size} van de{' '}
                        {questions.length} vragen beantwoord.
                      </p>
                    </div>
                    <div className="flex gap-3" data-id="element-1001" data-mp="7lok0ske4onr">
                      <Button onClick={() => setShowExitConfirm(false)} variant="primary" size="lg" fullWidth data-id="element-1002" data-mp="2ofwecgh3sf4">
                        Doorgaan
                      </Button>
                      <Button onClick={() => {
                    setShowExitConfirm(false);
                    onExit();
                  }} variant="secondary" size="lg" fullWidth className="text-rose-600 border-rose-200 hover:bg-rose-50" data-id="element-1003" data-mp="3ktj7cdt4eyng">
                        Stoppen
                      </Button>
                    </div>
                  </div>
                </Card>
              </motion.div>
            </>}
        </AnimatePresence>
      </div>
    </div>;
}